import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { isManagerTier, isEmployeeTier } from "@/lib/roles";
import { Sun, Moon, AlertTriangle, ArrowRight, Clock, ClipboardCheck, ChevronDown, ChevronUp, Sparkles, Truck } from "lucide-react";

const STATUS_LABELS = {
  not_opened: { label: "Not Opened", cls: "status-neutral" },
  opening_in_progress: { label: "Opening in Progress", cls: "status-progress" },
  open: { label: "Open", cls: "status-open" },
  closing_in_progress: { label: "Closing in Progress", cls: "status-progress" },
  closed: { label: "Closed", cls: "status-closed" },
};

const DC_STATUS = {
  assigned:            { label: "Assigned",            cls: "text-[color:var(--gold)] border-[color:var(--gold)]/40" },
  in_progress:         { label: "In Progress",         cls: "text-blue-300 border-blue-400/40" },
  submitted:           { label: "Waiting Approval",    cls: "text-purple-300 border-purple-400/40" },
  correction_required: { label: "Correction Required", cls: "text-[color:var(--warning)] border-[color:var(--warning)]/40" },
};

export default function Dashboard() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [now, setNow] = useState(new Date());
  const [showHistory, setShowHistory] = useState(false);

  useEffect(() => {
    api.get("/dashboard").then(({ data }) => setData(data));
    const id = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(id);
  }, []);

  if (!data) return <div className="text-[color:var(--text-secondary)]">Loading dashboard…</div>;

  const s = STATUS_LABELS[data.store_status] || STATUS_LABELS.not_opened;
  const settings = data.settings || {};
  const dayKey = ["sun","mon","tue","wed","thu","fri","sat"][new Date().getDay()];
  const sched = settings.schedule?.[dayKey] || { open: "10:00", close: "22:00" };
  const openPct = data.opening_report?.completion_pct ?? 0;
  const closePct = data.closing_report?.completion_pct ?? 0;

  const managerDC = (data.my_display_checks || []);
  const latestScore = data.latest_display_score;
  const history = data.display_history || [];
  const isManager = isManagerTier(user?.role);
  const isEmployee = isEmployeeTier(user?.role);
  const showReceiving = ["admin", "gm"].includes(user?.role) || user?.role === "inventory_manager" || user?.role === "manager" || user?.role === "assistant_manager";

  return (
    <div className="space-y-8" data-testid="dashboard-root">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
        <div>
          <div className="label-caps">House of Spells · Times Square</div>
          <h1 className="font-heading text-4xl sm:text-5xl mt-1" data-testid="dashboard-title">
            Good {timeGreeting()}, {user?.name?.split(" ")[0]}
          </h1>
          <div className="text-[color:var(--text-secondary)] mt-1 flex items-center gap-2">
            <Clock size={14}/> {now.toLocaleString()}
          </div>
        </div>
        <div className={`status-pill ${s.cls}`} data-testid="dashboard-store-status">{s.label}</div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        <StatCard icon={<Sun size={18}/>} label="Today's Opening" value={data.opening_report ? `${openPct}%` : "Not started"}
                  hint={data.opening_report ? data.opening_report.report_number : `Scheduled ${sched.open}`}
                  testid="stat-opening" />
        <StatCard icon={<Moon size={18}/>} label="Today's Closing" value={data.closing_report ? `${closePct}%` : "Not started"}
                  hint={data.closing_report ? data.closing_report.report_number : `Scheduled ${sched.close}`}
                  testid="stat-closing" />
        <StatCard icon={<AlertTriangle size={18}/>} label="Open Issues" value={String(data.open_issues_count)}
                  hint={data.recent_issues?.[0]?.description?.slice(0, 60) || "No active issues"}
                  testid="stat-issues" />
      </div>

      {showReceiving && <InventoryReceivingCard />}

      {/* --- Manager: My Display Checks assigned to me --- */}
      {isManager && managerDC.length > 0 && (
        <div className="hos-card p-6" data-testid="dash-my-display-checks">
          <div className="flex items-center justify-between mb-3">
            <div>
              <div className="label-caps">Assigned to you</div>
              <h3 className="font-heading text-2xl flex items-center gap-2"><ClipboardCheck size={20}/> My Display Checks</h3>
            </div>
            <Link to="/display" className="text-xs text-[color:var(--gold)] hover:underline">View all →</Link>
          </div>
          <ul className="space-y-2">
            {managerDC.map((c) => {
              const meta = DC_STATUS[c.status] || { label: c.status, cls: "" };
              const pct = c.zones_total ? Math.round(100 * c.zones_done / c.zones_total) : 0;
              const to = c.status === "submitted" ? `/display/${c.id}` : `/display/${c.id}/run`;
              return (
                <li key={c.id}>
                  <Link to={to} className="block p-3 rounded-md border border-[color:var(--border)] hover:border-[color:var(--gold)] transition" data-testid={`dash-dc-${c.id}`}>
                    <div className="flex items-center justify-between flex-wrap gap-2">
                      <div>
                        <div className="font-semibold">{c.check_number}</div>
                        <div className="text-xs text-[color:var(--text-secondary)]">{c.store_name} · {c.inspection_date}</div>
                      </div>
                      <span className={`text-[10px] px-2 py-1 rounded-full border ${meta.cls}`}>{meta.label}</span>
                    </div>
                    <div className="mt-2 flex items-center gap-2 text-xs">
                      <span className="text-[color:var(--text-secondary)]">{c.zones_done}/{c.zones_total} zones</span>
                      <div className="flex-1 h-1.5 bg-[color:var(--bg-surface-2)] rounded-full overflow-hidden">
                        <div className="h-full bg-[color:var(--gold)]" style={{ width: `${pct}%` }}/>
                      </div>
                      <span className="text-[color:var(--gold)] w-9 text-right">{pct}%</span>
                    </div>
                  </Link>
                </li>
              );
            })}
          </ul>
        </div>
      )}

      {/* --- Zone Owner personal scores + report links --- */}
      <div className="flex flex-wrap gap-2">
        <Link to="/my-display-scores" className="hos-btn-outline flex items-center gap-2 text-sm" data-testid="dash-my-scores-link">
          <Sparkles size={14}/> My Display Scores
        </Link>
        <Link to="/my-display-reports" className="hos-btn-outline flex items-center gap-2 text-sm" data-testid="dash-my-reports-link">
          My Display Report
        </Link>
        <Link to="/damages/new" className="hos-btn-outline flex items-center gap-2 text-sm text-[color:var(--warning)] border-[color:var(--warning)]/40" data-testid="dash-report-damage-btn">
          Report Damage
        </Link>
        <Link to="/damages" className="hos-btn-outline flex items-center gap-2 text-sm" data-testid="dash-damage-log-link">
          Damage Log
        </Link>
      </div>

      {/* --- Everyone: Latest Display Score + history --- */}
      {latestScore && (
        <div className="hos-card p-6" data-testid="dash-latest-display-score">
          <div className="flex items-center justify-between mb-3">
            <div>
              <div className="label-caps">Latest Display Score</div>
              <h3 className="font-heading text-2xl flex items-center gap-2"><Sparkles size={20}/> {latestScore.check_number}</h3>
            </div>
            <ScoreBadge score={latestScore.score}/>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
            <Info label="Store" value={latestScore.store_name}/>
            <Info label="Inspection Date" value={latestScore.inspection_date}/>
            <Info label="Manager" value={latestScore.manager_name}/>
            <Info label="Submitted" value={formatDateTime(latestScore.submitted_at)}/>
          </div>
          {!isEmployee && (
            <Link to={`/display/${latestScore.id}`} className="text-xs text-[color:var(--gold)] hover:underline mt-3 inline-flex items-center gap-1" data-testid="dash-open-latest-dc">
              Open report <ArrowRight size={12}/>
            </Link>
          )}
          {history.length > 1 && (
            <div className="mt-4 pt-4 border-t border-[color:var(--border)]">
              <button className="flex items-center gap-2 text-xs text-[color:var(--text-secondary)] hover:text-[color:var(--gold)]"
                      onClick={() => setShowHistory((v) => !v)} data-testid="dash-toggle-history">
                {showHistory ? <ChevronUp size={14}/> : <ChevronDown size={14}/>}
                {showHistory ? "Hide history" : `Show history (${history.length - 1} previous)`}
              </button>
              {showHistory && (
                <ul className="mt-3 divide-y divide-[color:var(--border)]" data-testid="dash-history-list">
                  {history.slice(1).map((h) => (
                    <li key={h.id} className="py-2 flex items-center justify-between gap-3 text-sm">
                      <div className="min-w-0 flex-1">
                        <div className="font-semibold truncate">{h.check_number}</div>
                        <div className="text-[11px] text-[color:var(--text-secondary)]">
                          {h.store_name} · {h.inspection_date} · {formatDateTime(h.submitted_at)}
                        </div>
                      </div>
                      <ScoreBadge score={h.score} small/>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </div>
      )}

      {!isEmployee && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <ActionCard to="/opening" title="Start Store Opening" desc="Walk through 26 checkpoints to open safely."
                      accent="var(--gold)" testid="action-opening"/>
          <ActionCard to="/closing" title="Start Store Closing" desc="Secure, reconcile, and lock the store."
                      accent="var(--gold)" testid="action-closing"/>
          <ActionCard to="/schedules" title={isEmployee ? "My Schedule" : "Employee Schedules"}
                      desc={user?.role === "gm" ? "Approve, publish, and manage weekly schedules." : isEmployee ? "View your published shifts." : "Create weekly schedules for approval."}
                      accent="var(--gold)" testid="action-schedules"/>
          <ActionCard to="/issues" title="Report an Issue" desc="Log maintenance, safety, or ops issues."
                      accent="var(--warning)" testid="action-issue"/>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="hos-card p-6 lg:col-span-2">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-heading text-2xl">Manager on Duty</h3>
            <div className="label-caps">Live</div>
          </div>
          <div className="text-[color:var(--text-secondary)]">
            {data.manager_on_duty || "No manager checked in for today."}
          </div>
          <div className="mt-6 grid grid-cols-2 gap-4">
            <div>
              <div className="label-caps">Scheduled Open</div>
              <div className="text-2xl font-heading">{sched.open}</div>
            </div>
            <div>
              <div className="label-caps">Scheduled Close</div>
              <div className="text-2xl font-heading">{sched.close}</div>
            </div>
          </div>
        </div>

        <div className="hos-card p-6">
          <h3 className="font-heading text-2xl mb-4">Recent Issues</h3>
          {(data.recent_issues || []).length === 0 && (
            <div className="text-[color:var(--text-secondary)] text-sm">Nothing to report. All clear.</div>
          )}
          <ul className="space-y-3">
            {(data.recent_issues || []).map((i) => (
              <li key={i.id} className="border-l-2 pl-3 border-[color:var(--warning)]">
                <div className="text-sm text-[color:var(--text-primary)]">{i.description}</div>
                <div className="text-xs text-[color:var(--text-secondary)]">
                  {i.category} · {i.urgency}
                </div>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}


function InventoryReceivingCard() {
  const [stats, setStats] = useState(null);
  useEffect(() => {
    api.get("/inventory_dashboard").then(({ data }) => setStats(data)).catch(() => {});
  }, []);
  if (!stats) return null;
  return (
    <div className="hos-card p-6" data-testid="dash-inventory-receiving">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
        <div>
          <div className="label-caps">Inventory Receiving</div>
          <h3 className="font-heading text-2xl flex items-center gap-2"><Truck size={20}/> New Shipment</h3>
        </div>
        <Link to="/receiving" className="hos-btn-primary flex items-center gap-2 text-sm" data-testid="dash-receive-shipment-btn">
          <Truck size={14}/> Receive Shipment
        </Link>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <MiniStat label="Today's Receipts" value={stats.today_receipts}/>
        <MiniStat label="Pallets" value={stats.today_pallets}/>
        <MiniStat label="Boxes" value={stats.today_boxes}/>
        <MiniStat label="With Issues" value={stats.today_with_issues} warn={stats.today_with_issues > 0}/>
      </div>
      {(stats.recent || []).length > 0 && (
        <div className="mt-4 pt-4 border-t border-[color:var(--border)]">
          <div className="label-caps mb-2">Recent</div>
          <ul className="space-y-1">
            {stats.recent.slice(0, 4).map((r) => (
              <li key={r.id}>
                <Link to={["accepted", "accepted_with_issues", "cancelled"].includes(r.status) ? `/receiving/${r.id}` : `/receiving/${r.id}/edit`}
                      className="flex items-center justify-between p-2 rounded hover:bg-[color:var(--bg-surface-2)]/40 text-sm">
                  <div className="min-w-0">
                    <span className="font-semibold">{r.reference_number}</span>
                    <span className="text-[color:var(--text-secondary)]"> · {r.supplier_name || "—"}</span>
                  </div>
                  <span className="text-xs text-[color:var(--text-secondary)]">{r.total_pallets || 0}p / {r.total_boxes || 0}b</span>
                </Link>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function MiniStat({ label, value, warn }) {
  return (
    <div className="rounded-md border border-[color:var(--border)] p-3">
      <div className="label-caps text-[10px]">{label}</div>
      <div className={`font-heading text-2xl ${warn ? "text-[color:var(--warning)]" : "text-[color:var(--gold)]"}`}>{value ?? 0}</div>
    </div>
  );
}

function StatCard({ icon, label, value, hint, testid }) {
  return (
    <div className="hos-card p-5" data-testid={testid}>
      <div className="flex items-center gap-2 label-caps">{icon}{label}</div>
      <div className="font-heading text-4xl mt-2">{value}</div>
      <div className="text-xs text-[color:var(--text-secondary)] mt-1">{hint}</div>
    </div>
  );
}

function ActionCard({ to, title, desc, accent, testid }) {
  return (
    <Link to={to} data-testid={testid} className="hos-card p-6 flex items-center justify-between hover:border-[color:var(--gold)] transition group">
      <div>
        <div className="font-heading text-xl" style={{ color: accent }}>{title}</div>
        <div className="text-sm text-[color:var(--text-secondary)] mt-1">{desc}</div>
      </div>
      <ArrowRight className="text-[color:var(--text-secondary)] group-hover:text-[color:var(--gold)] group-hover:translate-x-1 transition" />
    </Link>
  );
}

function ScoreBadge({ score, small }) {
  if (score === null || score === undefined) {
    return <div className={`${small ? "text-xs" : "text-base"} text-[color:var(--text-secondary)]`}>—</div>;
  }
  const color = score >= 85 ? "text-emerald-400 border-emerald-400/40 bg-emerald-500/10"
              : score >= 60 ? "text-[color:var(--gold)] border-[color:var(--gold)]/40 bg-[color:var(--gold)]/10"
              : "text-[color:var(--error)] border-[color:var(--error)]/40 bg-[color:var(--error)]/10";
  return (
    <div className={`inline-flex items-baseline gap-1 rounded-md border px-3 py-1 ${color}`} data-testid="score-badge">
      <span className={`font-heading ${small ? "text-lg" : "text-3xl"}`}>{score}</span>
      <span className="text-[10px] uppercase">/100</span>
    </div>
  );
}

function Info({ label, value }) {
  return (
    <div>
      <div className="label-caps">{label}</div>
      <div className="text-[color:var(--text-primary)]">{value || "—"}</div>
    </div>
  );
}

function formatDateTime(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleString();
  } catch (_) { return iso; }
}

function timeGreeting() {
  const h = new Date().getHours();
  if (h < 12) return "morning";
  if (h < 18) return "afternoon";
  return "evening";
}
